const DiscountPgAdapter = require('./pg-adapter')
const { isUuid } = require('../freshsales/tools')
const DiscountResolver = require('./discount-resolver')
const { MAX_DISCOUNT_RATIO, withinDiscountLimit } = require('./discount-limit')

// What a planner may edit. status, approved_by and rejected_by are deliberately absent:
// a discount only changes state through changeStatus, which is approver-only.
const UPDATABLE_FIELDS = {
  description: 'description',
  percentage: 'discount_percentage',
  amount: 'discount_amount',
  currency: 'currency',
  discountLevel: 'application_level',
  startDate: 'start_date',
  endDate: 'end_date'
}

const assertAllResolved = (level, ids, lookupIds, resolvedIds) => {
  const resolved = new Set(resolvedIds)
  const notFound = ids.filter((id, index) => !resolved.has(lookupIds[index]))
  if (notFound.length > 0) {
    const error = new Error(`Discount ${level} items not found`)
    error.errors = notFound
    error.status = 404
    throw error
  }
  return resolvedIds
}

class DiscountApi {
  constructor (state, pgConnection, agaveAdapter, logger, priceAdapter) {
    this.agaveAdapter = agaveAdapter
    this.pgAdapter = new DiscountPgAdapter(pgConnection)
    this.logger = logger
    this.priceAdapter = priceAdapter
    this.resolver = new DiscountResolver(pgConnection)
  }

  getResolver (discountLevel) {
    return {
      location: async (ids) => {
        const resolvedLocationIds = await this.pgAdapter.getLocations(ids)
        const itemIds = assertAllResolved('location', ids, ids, resolvedLocationIds)
        return itemIds.map(id => ({ location_id: id }))
      },
      product: async (ids) => {
        const skuIds = ids.map(id => id.replace('product:', ''))
        const resolvedSkuIds = await this.pgAdapter.getProductSkus(skuIds)
        const itemIds = assertAllResolved('product', ids, skuIds, resolvedSkuIds)
        return itemIds.map(id => ({ product_sku_id: id }))
      },
      market: async (ids) => {
        const resolvedMarketIds = await this.pgAdapter.getMarkets(ids)
        const itemIds = assertAllResolved('market', ids, ids, resolvedMarketIds)
        return itemIds.map(id => ({ market_id: id }))
      },
      order: async (ids) => {
        return ids.map(id => ({ order_id: id }))
      },
      organisation: async (ids) => {
        // data_organisation.id is a uuid column, so a malformed id makes postgres
        // raise 22P02 - a 500 - instead of telling the caller which id was wrong
        const malformed = ids.filter(id => !isUuid(id))
        if (malformed.length > 0) {
          const error = new Error('Discount organisation items must be uuids')
          error.errors = malformed
          error.status = 400
          throw error
        }
        const resolvedOrganisationIds = await this.pgAdapter.getOrganisations(ids)
        const itemIds = assertAllResolved('organisation', ids, ids, resolvedOrganisationIds)
        return itemIds.map(id => ({ organisation_id: id }))
      }
    }[discountLevel]
  }

  // Resolves the items for a level, then optionally narrows a product discount to one
  // location, so each item carries both the sku and the location it applies at.
  async resolveItems (discountLevel, items, locationId) {
    const resolverFunction = this.getResolver(discountLevel)
    if (!resolverFunction) {
      this.logger.error({ code: 'Discount level resolver not found', discountLevel })
      const error = new Error('Discount level not supported')
      error.status = 400
      throw error
    }

    const resolvedItems = await resolverFunction(items.map(item => item.id))
    if (!locationId) {
      return resolvedItems
    }
    if (discountLevel !== 'product') {
      const error = new Error('Only a product level discount can be scoped to a location')
      error.status = 400
      throw error
    }

    const [scope] = await this.getResolver('location')([locationId])
    return resolvedItems.map(item => ({ ...item, location_id: scope.location_id }))
  }

  /**
   * Rejects a discount that takes more than MAX_DISCOUNT_RATIO off a current sell price.
   * A percentage is checked at every level. A fixed amount can only be measured against
   * named products, so other levels are left to DiscountResolver.resolveOrderDiscount, which skips them per sku.
   */
  async assertWithinDiscountLimit ({ percentage, amount, discountLevel, productIds: rawProductIds = [] }) {
    // Items may be sent with or without the prefix the price list keys its skus by
    const productIds = rawProductIds.map(id => `product:${`${id}`.replace('product:', '')}`)
    const limitPercent = MAX_DISCOUNT_RATIO * 100
    if (percentage != null && Number(percentage) > limitPercent) {
      const error = new Error(`A discount can be at most ${limitPercent}% of the sell price`)
      error.status = 400
      throw error
    }

    if (amount == null || discountLevel !== 'product' || productIds.length === 0) {
      return
    }

    const { prices } = await this.priceAdapter.getPriceList({ skus: productIds })
    const sellPrices = new Map(prices.map(price => [price.sku, price.sellprice]))
    const fixedDiscount = { discountPercentage: null, discountAmount: Number(amount) }
    const rejected = productIds.filter(productId => !withinDiscountLimit(fixedDiscount, sellPrices.get(productId)))
    if (rejected.length > 0) {
      const error = new Error(`A discount can be at most ${limitPercent}% of each product's current sell price`)
      error.errors = rejected.map(productId => ({
        productId,
        // null when the product has no current price to measure against
        sellPrice: sellPrices.get(productId) == null ? null : sellPrices.get(productId)
      }))
      error.status = 400
      throw error
    }
  }

  async create ({entry, createdBy}) {
    const { discountLevel } = entry
    await this.assertWithinDiscountLimit({
      percentage: entry.percentage,
      amount: entry.amount,
      discountLevel,
      productIds: (entry.items || []).map(item => item.id)
    })
    const resolvedItems = await this.resolveItems(discountLevel, entry.items, entry.locationId)
    const discountRow = {
      description: entry.description,
      discount_percentage: entry.percentage,
      discount_amount: entry.amount,
      currency: entry.currency,
      application_level: discountLevel,
      status: 'pending',
      is_active: false,
      start_date: entry.startDate,
      end_date: entry.endDate,
      created_by: createdBy,
      updated_by: createdBy,
      approved_by: null,
      rejected_by: null
    }
    const response = await this.pgAdapter.create(discountRow, resolvedItems)
    return response
  }

  async getById (id) {
    const row = await this.pgAdapter.get(id)
    if (!row) {
      const error = new Error('Discount not found')
      error.status = 404
      throw error
    }
    return row
  }

  async getList (page, limit, sort) {
    const response = await this.pgAdapter.getList(page, limit, sort)
    return {
      results: response.data,
      count: response.paging.total
    }
  }

  // Both update and delete are only allowed while a discount is still pending.
  async getPendingById (id) {
    const discount = await this.getById(id)
    if (discount.status !== 'pending') {
      const error = new Error(`Discount has already been marked as '${discount.status}'`)
      error.status = 409
      throw error
    }
    return discount
  }

  async update (id, entry, updatedBy) {
    const discount = await this.getPendingById(id)

    const discountRow = { updated_by: updatedBy }
    Object.keys(UPDATABLE_FIELDS).forEach(field => {
      if (entry[field] !== undefined) {
        discountRow[UPDATABLE_FIELDS[field]] = entry[field]
      }
    })

    const discountLevel = entry.discountLevel || discount.application_level
    // Check what the discount will be after this edit: sent values win over stored ones
    await this.assertWithinDiscountLimit({
      percentage: entry.percentage !== undefined ? entry.percentage : discount.discount_percentage,
      amount: entry.amount !== undefined ? entry.amount : discount.discount_amount,
      discountLevel,
      productIds: entry.items
        ? entry.items.map(item => item.id)
        : (discount.items || []).map(item => `product:${item.item_id}`)
    })
    if (entry.items) {
      const resolvedItems = await this.resolveItems(discountLevel, entry.items, entry.locationId)
      await this.pgAdapter.replaceItems(id, resolvedItems)
    } else if (entry.discountLevel && entry.discountLevel !== discount.application_level) {
      // The existing items belong to the old level, so they would no longer resolve.
      const error = new Error('Changing the discount level requires new items')
      error.status = 400
      throw error
    }

    await this.pgAdapter.update(id, discountRow, 'pending')
    return this.getById(id)
  }

  async delete (id) {
    await this.getPendingById(id)
    return this.pgAdapter.delete(id)
  }

  // Activation is the one change an approved discount still accepts, and only an
  // approver may make it. Everything else about it is frozen by then.
  async changeActivation (id, isActive, updatedBy) {
    const discount = await this.getById(id)
    if (discount.status !== 'approved') {
      const error = new Error(`Only an approved discount can be activated, this one is '${discount.status}'`)
      error.status = 409
      throw error
    }

    await this.pgAdapter.update(id, { is_active: isActive, updated_by: updatedBy }, 'approved')
    return this.getById(id)
  }

  async changeStatus (id, status, updatedBy) {
    const discount = await this.getById(id)
    if (discount.status !== 'pending') {
      const error = new Error(`Discount has already been marked as '${discount.status}'`)
      error.status = 409
      throw error
    }
    const updateValue = {
      updated_by: updatedBy,
      ...(status === 'approved'
        ? { approved_by: updatedBy, status, is_active: true }
        : status === 'rejected'
          ? { rejected_by: updatedBy, status }
          : {}
      )
    }
    const update = await this.pgAdapter.update(id, updateValue, 'pending')
    return update
  }
}

module.exports = DiscountApi
